"use client"

import {
    useState,
    useEffect,
    useCallback,
    useRef,
    type CSSProperties,
} from "react"
const useIsStaticRenderer = () => false

interface Slide {
    image?: { src?: string; srcSet?: string; alt?: string }
    title?: string
}

type AutoplayDir = "leftToRight" | "rightToLeft"
type TitleCorner = "topLeft" | "topRight" | "bottomLeft" | "bottomRight"

interface Smooth3DSlideshowProps {
    slides?: Slide[]
    cardWidth?: number
    cardHeight?: number
    radius?: number
    tilt?: number
    sideTilt?: number
    gap?: number
    opacity?: number
    inactiveCardsPerSide?: number
    initialIndex?: number
    onActiveChange?: (index: number) => void
    transition?: any
    autoplay?: boolean
    autoplayDirection?: AutoplayDir
    showTitle?: boolean
    titleFont?: CSSProperties
    titleColor?: string
    titlePosition?: {
        position?: TitleCorner
        paddingLeft?: number
        paddingRight?: number
        paddingTop?: number
        paddingBottom?: number
    }
    style?: CSSProperties
}

const DEFAULT_SLIDES: Slide[] = [
    {
        image: {
            src: "https://imagedelivery.net/IEUjvl3YUlxY-MrTpOAWDQ/316d1761-fd79-4ca9-b8d4-f2bb20521a00/w=800",
        },
        title: "For Sitting\nMetal\nMinimal",
    },
    {
        image: {
            src: "https://imagedelivery.net/IEUjvl3YUlxY-MrTpOAWDQ/aeaa0756-9647-4f6c-d900-204bd25e4a00/w=800",
        },
        title: "For Living\nConcrete\nForm",
    },
    {
        image: {
            src: "https://imagedelivery.net/IEUjvl3YUlxY-MrTpOAWDQ/34ce1842-4b7a-4d52-0302-38582c341700/w=800",
        },
        title: "For Working\nSteel\nClean",
    },
]

// Fixed internals (no longer exposed as controls).
const PERSPECTIVE = 1600
const SCALE_STEP = 0.16
// In a preserve-3d context paint order follows 3D position, not z-index, so the
// centre is pushed nearest the viewer and neighbours fall back behind it.
const DEPTH = 240

// Derive a CSS transition (duration + easing) from a Framer Transition value.
function cssTransition(t: any): { dur: number; ease: string } {
    const dur = t && typeof t.duration === "number" ? t.duration : 0.6
    let ease = "cubic-bezier(0.22, 1, 0.36, 1)"
    const e = t?.ease
    if (Array.isArray(e) && e.length === 4) {
        ease = `cubic-bezier(${e[0]}, ${e[1]}, ${e[2]}, ${e[3]})`
    } else if (typeof e === "string") {
        const map: Record<string, string> = {
            linear: "linear",
            easeIn: "ease-in",
            easeOut: "ease-out",
            easeInOut: "ease-in-out",
        }
        ease = map[e] || "ease"
    }
    return { dur, ease }
}

/**
 * Smooth 3D Slideshow
 *
 * A 3D coverflow: the active card sits upright in the spotlight while its
 * neighbours tilt back in perspective. Click any card to smoothly bring it to
 * centre. Recreated after Tanya Prokofieva's Framer original.
 *
 * @framerIntrinsicWidth 900
 * @framerIntrinsicHeight 560
 *
 * @framerSupportedLayoutWidth any-prefer-fixed
 * @framerSupportedLayoutHeight any-prefer-fixed
 */
export default function Smooth3DSlideshow(props: Smooth3DSlideshowProps) {
    props = { ...COMPONENT_DEFAULTS, ...props }
    const {
        slides = DEFAULT_SLIDES,
        cardWidth = 557,
        cardHeight = 420,
        radius = 0,
        tilt = 7,
        sideTilt = 7,
        gap = 7,
        opacity = 65,
        inactiveCardsPerSide = 1,
        initialIndex = 0,
        onActiveChange,
        transition,
        autoplay = false,
        autoplayDirection = "rightToLeft",
        showTitle = true,
        titleFont,
        titleColor = "#ffffff",
        titlePosition,
        style,
    } = props

    const tp = titlePosition || {}
    const corner: TitleCorner = tp.position || "bottomLeft"
    const isTop = corner === "topLeft" || corner === "topRight"
    const isRight = corner === "topRight" || corner === "bottomRight"
    const padLeft = tp.paddingLeft ?? 22
    const padRight = tp.paddingRight ?? 22
    const padTop = tp.paddingTop ?? 24
    const padBottom = tp.paddingBottom ?? 24

    const isStatic = useIsStaticRenderer()
    const list = slides && slides.length ? slides : DEFAULT_SLIDES
    const n = list.length

    // Product journeys have real first/last pages: reaching an edge stops
    // there instead of wrapping to the opposite end.
    const loop = false
    const normalizeIndex = useCallback(
        (index: number) => Math.max(0, Math.min(n - 1, index)),
        [n]
    )
    const [active, setActive] = useState(() => normalizeIndex(initialIndex))
    const activeRef = useRef(active)
    const setActiveIndex = useCallback(
        (nextIndex: number) => {
            const next = normalizeIndex(nextIndex)
            if (next === activeRef.current) return
            activeRef.current = next
            setActive(next)
            onActiveChange?.(next)
        },
        [normalizeIndex, onActiveChange]
    )

    // Keep active valid if the slide list changes.
    useEffect(() => {
        const next = normalizeIndex(activeRef.current)
        activeRef.current = next
        setActive(next)
    }, [n, normalizeIndex])

    // Lock input while a card is mid-move; release once it settles, so rapid
    // clicks/keys don't stack up and look jittery. Duration comes from the
    // transition (default 0.6s).
    const moveDur =
        transition && typeof transition.duration === "number"
            ? transition.duration
            : 0.6
    const lockRef = useRef(false)
    const lock = useCallback(() => {
        lockRef.current = true
        window.setTimeout(
            () => {
                lockRef.current = false
            },
            Math.max(50, moveDur * 1000)
        )
    }, [moveDur])

    const step = useCallback(
        (dir: number) => {
            if (lockRef.current) return
            const next = normalizeIndex(activeRef.current + dir)
            if (next === activeRef.current) return
            lock()
            setActiveIndex(next)
        },
        [lock, normalizeIndex, setActiveIndex]
    )

    const handleCardClick = useCallback(
        (i: number) => {
            if (isStatic || autoplay || lockRef.current) return
            const next = normalizeIndex(i === activeRef.current ? i + 1 : i)
            if (next === activeRef.current) return
            lock()
            setActiveIndex(next)
        },
        [isStatic, autoplay, lock, normalizeIndex, setActiveIndex]
    )

    // Autoplay — the transition's Delay drives the time each card holds.
    const delay =
        transition && typeof transition.delay === "number"
            ? transition.delay
            : 2.5
    useEffect(() => {
        if (isStatic || !autoplay || n < 2) return
        const ms = Math.max(0.3, delay) * 1000
        const dir = autoplayDirection === "leftToRight" ? -1 : 1
        const id = window.setInterval(() => step(dir), ms)
        return () => window.clearInterval(id)
    }, [isStatic, autoplay, autoplayDirection, delay, n, step])

    const onKeyDown = useCallback(
        (e: React.KeyboardEvent) => {
            if (e.key === "ArrowRight") {
                e.preventDefault()
                step(1)
            } else if (e.key === "ArrowLeft") {
                e.preventDefault()
                step(-1)
            }
        },
        [step]
    )

    // 拖动时直接用指针位移驱动卡片，不使用惯性。松手后先换算到最近
    // 的逻辑卡片，再从完全相同的视觉位置平滑吸附到中心。
    const [dragOffset, setDragOffset] = useState(0)
    const [dragging, setDragging] = useState(false)
    const rootRef = useRef<HTMLDivElement | null>(null)
    const dragOffsetRef = useRef(0)
    const dragRef = useRef<{ pointerId: number; startX: number } | null>(null)
    const suppressClickRef = useRef(false)
    const snapFrameRef = useRef<number | null>(null)
    const dragStepPx = Math.max(48, Math.abs(gap * 30))

    const setDragProgress = useCallback((progress: number) => {
        const lower = -activeRef.current
        const upper = n - 1 - activeRef.current
        const rubberBand = (distance: number) =>
            0.36 * (1 - Math.exp(-distance / 0.55))
        let resisted = progress
        if (progress < lower) resisted = lower - rubberBand(lower - progress)
        if (progress > upper) resisted = upper + rubberBand(progress - upper)
        dragOffsetRef.current = resisted
        setDragOffset(resisted)
    }, [n])

    const onPointerDown = useCallback(
        (event: React.PointerEvent<HTMLDivElement>) => {
            if (isStatic || autoplay || n < 2 || lockRef.current) return
            if (event.pointerType === "mouse" && event.button !== 0) return
            if ((event.target as HTMLElement).closest("button, a, input, select, textarea")) return
            if (snapFrameRef.current != null) {
                window.cancelAnimationFrame(snapFrameRef.current)
                snapFrameRef.current = null
            }
            suppressClickRef.current = false
            dragRef.current = { pointerId: event.pointerId, startX: event.clientX }
            setDragProgress(0)
            setDragging(true)
            event.currentTarget.setPointerCapture(event.pointerId)
        },
        [autoplay, isStatic, n, setDragProgress]
    )

    const onPointerMove = useCallback(
        (event: React.PointerEvent<HTMLDivElement>) => {
            const drag = dragRef.current
            if (!drag || drag.pointerId !== event.pointerId) return
            const progress = -(event.clientX - drag.startX) / dragStepPx
            if (Math.abs(progress) > 0.04) suppressClickRef.current = true
            setDragProgress(progress)
        },
        [dragStepPx, setDragProgress]
    )

    const settleDrag = useCallback(() => {
        setDragging(false)
        const progress = dragOffsetRef.current
        if (Math.abs(progress) <= 0.04) {
            setDragProgress(0)
            return
        }
        /* 边界外只显示渐增阻尼的橡皮筋位移；松手时先裁回真实卡位范围，
           因而首尾不会循环，视觉位置再自然回弹到最近的有效卡片。 */
        const bounded = Math.max(
            -activeRef.current,
            Math.min(n - 1 - activeRef.current, progress)
        )
        const delta = Math.round(bounded)
        const residual = bounded - delta
        setActiveIndex(activeRef.current + delta)
        setDragProgress(residual)
        lock()
        snapFrameRef.current = window.requestAnimationFrame(() => {
            snapFrameRef.current = null
            setDragProgress(0)
        })
    }, [lock, n, setActiveIndex, setDragProgress])

    const finishDrag = useCallback(
        (event: React.PointerEvent<HTMLDivElement>) => {
            const drag = dragRef.current
            if (!drag || drag.pointerId !== event.pointerId) return
            dragRef.current = null
            if (event.currentTarget.hasPointerCapture(event.pointerId)) {
                event.currentTarget.releasePointerCapture(event.pointerId)
            }
            settleDrag()
        },
        [settleDrag]
    )

    // 个别 WebView 只派发传统 mouse/touch 事件；与 Pointer Events 共用
    // 同一状态机，Pointer Events 已接管时这些回退分支会自动忽略。
    const onMouseDown = useCallback(
        (event: React.MouseEvent<HTMLDivElement>) => {
            if (dragRef.current || isStatic || autoplay || n < 2 || lockRef.current || event.button !== 0) return
            if ((event.target as HTMLElement).closest("button, a, input, select, textarea")) return
            suppressClickRef.current = false
            dragRef.current = { pointerId: -1, startX: event.clientX }
            setDragProgress(0)
            setDragging(true)
        },
        [autoplay, isStatic, n, setDragProgress]
    )
    const onMouseMove = useCallback(
        (event: React.MouseEvent<HTMLDivElement>) => {
            const drag = dragRef.current
            if (!drag || drag.pointerId !== -1) return
            const progress = -(event.clientX - drag.startX) / dragStepPx
            if (Math.abs(progress) > 0.04) suppressClickRef.current = true
            setDragProgress(progress)
        },
        [dragStepPx, setDragProgress]
    )
    const onMouseUp = useCallback(() => {
        if (dragRef.current?.pointerId !== -1) return
        dragRef.current = null
        settleDrag()
    }, [settleDrag])
    const onTouchStart = useCallback(
        (event: React.TouchEvent<HTMLDivElement>) => {
            if (dragRef.current || isStatic || autoplay || n < 2 || lockRef.current) return
            const touch = event.changedTouches[0]
            if (!touch || (event.target as HTMLElement).closest("button, a, input, select, textarea")) return
            suppressClickRef.current = false
            dragRef.current = { pointerId: -(touch.identifier + 2), startX: touch.clientX }
            setDragProgress(0)
            setDragging(true)
        },
        [autoplay, isStatic, n, setDragProgress]
    )
    const onTouchMove = useCallback(
        (event: React.TouchEvent<HTMLDivElement>) => {
            const drag = dragRef.current
            if (!drag || drag.pointerId >= -1) return
            const identifier = -drag.pointerId - 2
            const touch = Array.from(event.changedTouches).find((item) => item.identifier === identifier)
            if (!touch) return
            const progress = -(touch.clientX - drag.startX) / dragStepPx
            if (Math.abs(progress) > 0.04) suppressClickRef.current = true
            setDragProgress(progress)
        },
        [dragStepPx, setDragProgress]
    )
    const onTouchEnd = useCallback((event: React.TouchEvent<HTMLDivElement>) => {
        const drag = dragRef.current
        if (!drag || drag.pointerId >= -1) return
        const identifier = -drag.pointerId - 2
        if (!Array.from(event.changedTouches).some((item) => item.identifier === identifier)) return
        dragRef.current = null
        settleDrag()
    }, [settleDrag])

    const onRootClick = useCallback((event: MouseEvent) => {
        if (suppressClickRef.current) {
            suppressClickRef.current = false
            return
        }
        const target = event.target as HTMLElement
        if (target.closest("button, a, input, select, textarea")) return
        let node: HTMLElement | null = target
        let index = -1
        while (node && node !== rootRef.current && index < 0) {
            const label = node.getAttribute("aria-label")
            index = list.findIndex((slide) => slide.title === label)
            node = node.parentElement
        }
        if (index >= 0) handleCardClick(index)
    }, [handleCardClick, list])

    // Portal 内容在 React 事件树中不经过卡片节点，因此在 Coverflow 根节点
    // 使用原生监听，确保从卡面任意非交互区域起手都能驱动 3D 位移。
    useEffect(() => {
        const root = rootRef.current
        if (!root) return
        const pointerDown = (event: PointerEvent) => onPointerDown(event as any)
        const pointerMove = (event: PointerEvent) => onPointerMove(event as any)
        const pointerUp = (event: PointerEvent) => finishDrag(event as any)
        const mouseDown = (event: MouseEvent) => onMouseDown(event as any)
        const mouseMove = (event: MouseEvent) => onMouseMove(event as any)
        const mouseUp = () => onMouseUp()
        const touchStart = (event: TouchEvent) => onTouchStart(event as any)
        const touchMove = (event: TouchEvent) => onTouchMove(event as any)
        const touchEnd = (event: TouchEvent) => onTouchEnd(event as any)
        const click = (event: MouseEvent) => onRootClick(event)
        const preventNativeSelection = (event: Event) => event.preventDefault()
        root.addEventListener("pointerdown", pointerDown)
        root.addEventListener("pointermove", pointerMove)
        root.addEventListener("pointerup", pointerUp)
        root.addEventListener("pointercancel", pointerUp)
        root.addEventListener("mousedown", mouseDown)
        root.addEventListener("mousemove", mouseMove)
        root.addEventListener("mouseup", mouseUp)
        root.addEventListener("mouseleave", mouseUp)
        root.addEventListener("touchstart", touchStart, { passive: true })
        root.addEventListener("touchmove", touchMove, { passive: true })
        root.addEventListener("touchend", touchEnd)
        root.addEventListener("touchcancel", touchEnd)
        root.addEventListener("click", click)
        root.addEventListener("contextmenu", preventNativeSelection)
        root.addEventListener("selectstart", preventNativeSelection)
        root.addEventListener("dragstart", preventNativeSelection)
        return () => {
            root.removeEventListener("pointerdown", pointerDown)
            root.removeEventListener("pointermove", pointerMove)
            root.removeEventListener("pointerup", pointerUp)
            root.removeEventListener("pointercancel", pointerUp)
            root.removeEventListener("mousedown", mouseDown)
            root.removeEventListener("mousemove", mouseMove)
            root.removeEventListener("mouseup", mouseUp)
            root.removeEventListener("mouseleave", mouseUp)
            root.removeEventListener("touchstart", touchStart)
            root.removeEventListener("touchmove", touchMove)
            root.removeEventListener("touchend", touchEnd)
            root.removeEventListener("touchcancel", touchEnd)
            root.removeEventListener("click", click)
            root.removeEventListener("contextmenu", preventNativeSelection)
            root.removeEventListener("selectstart", preventNativeSelection)
            root.removeEventListener("dragstart", preventNativeSelection)
        }
    }, [finishDrag, onMouseDown, onMouseMove, onMouseUp, onPointerDown, onPointerMove, onRootClick, onTouchEnd, onTouchMove, onTouchStart])

    useEffect(() => () => {
        if (snapFrameRef.current != null) window.cancelAnimationFrame(snapFrameRef.current)
    }, [])

    const { dur, ease } = cssTransition(transition)
    const transitionCss = `transform ${dur}s ${ease}, opacity ${dur}s ${ease}`

    // Rounded scale 0–20: boxy at 0, fully rounded (pill on the short axis) at 20.
    const effectiveRadius =
        (Math.max(0, Math.min(20, radius)) / 20) *
        (Math.min(cardWidth, cardHeight) / 2)
    // Inactive opacity: 100% = fully visible, 0% = hidden. Overlay is the inverse.
    const dim = 1 - Math.max(0, Math.min(100, opacity)) / 100
    const visibleSideCards = Math.max(0, Math.round(inactiveCardsPerSide))
    const frontTravel = Math.max(44, Math.min(cardWidth * 0.17, 52))
    const rearStep = Math.max(12, Math.abs(gap * 12))
    const maskDuration = dur === 0 ? 0 : 0.5

    // 每个卡位内做正弦缓入缓出；中心到第一张侧卡使用长行程，
    // 后侧卡位之间使用短行程，形成前景展开、后景紧凑的 Coverflow 节奏。
    const easedSlot = (value: number) => {
        const sign = Math.sign(value)
        const absolute = Math.abs(value)
        const whole = Math.floor(absolute)
        const fraction = absolute - whole
        const eased = (1 - Math.cos(Math.PI * fraction)) / 2
        return sign * (whole + eased)
    }

    const mappedX = (value: number) => {
        const sign = Math.sign(value)
        const absolute = Math.abs(value)
        return sign * (absolute <= 1
            ? frontTravel * absolute
            : frontTravel + rearStep * (absolute - 1))
    }

    const rootStyle: CSSProperties = {
        ...(style || {}),
        position: "relative",
        width: "100%",
        height: "100%",
        minWidth: 320,
        minHeight: 360,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        perspective: `${PERSPECTIVE}px`,
        overflow: "hidden",
        outline: "none",
        touchAction: "pan-y",
        cursor: dragging ? "grabbing" : "grab",
        userSelect: "none",
    }

    return (
        <div
            ref={rootRef}
            style={rootStyle}
            tabIndex={0}
            role="group"
            aria-roledescription="carousel"
            onKeyDown={isStatic ? undefined : onKeyDown}
        >
            <div
                style={{
                    position: "relative",
                    width: cardWidth,
                    height: cardHeight,
                    transformStyle: "preserve-3d",
                }}
            >
                {list.map((slide, i) => {
                    let visualRel = i - active - dragOffset
                    if (loop) {
                        while (visualRel > n / 2) visualRel -= n
                        while (visualRel < -n / 2) visualRel += n
                    }
                    const ax = Math.abs(visualRel)
                    const visible = ax <= visibleSideCards + (dragging ? 1 : 0)
                    const motionRel = easedSlot(visualRel)
                    const motionAx = Math.abs(motionRel)
                    const isFront = ax <= 0.5
                    const sc = Math.max(0.4, 1 - motionAx * SCALE_STEP)
                    const tx = mappedX(motionRel)
                    const tz = -motionAx * DEPTH
                    const ry = -motionRel * tilt
                    const rz = motionRel * sideTilt
                    const src = slide.image?.src || ""

                    const cardStyle: CSSProperties = {
                        position: "absolute",
                        left: "50%",
                        top: "50%",
                        width: cardWidth,
                        height: cardHeight,
                        borderRadius: effectiveRadius,
                        overflow: "hidden",
                        transformStyle: "preserve-3d",
                        transformOrigin: "center center",
                        transform: `translate(-50%, -50%) translateX(${tx}px) translateZ(${tz}px) rotateY(${ry}deg) rotateZ(${rz}deg) scale(${sc})`,
                        transition: dragging
                            ? "box-shadow 0.5s ease-in-out"
                            : `${transitionCss}, box-shadow 0.5s ease-in-out`,
                        opacity: visible ? 1 : 0,
                        cursor: autoplay || isFront ? "default" : "pointer",
                        pointerEvents:
                            visible && !isStatic && !autoplay ? "auto" : "none",
                        backgroundColor: "#ffffff",
                        boxShadow: isFront
                            ? "0 20px 28px -18px rgba(35, 67, 112, 0.5)"
                            : "none",
                    }

                    return (
                        <div
                            key={i}
                            style={cardStyle}
                            aria-label={slide.title}
                            aria-hidden={!visible}
                        >
                            {src ? (
                                <img
                                    src={src}
                                    alt={slide.image?.alt || slide.title || ""}
                                    draggable={false}
                                    style={{
                                        position: "absolute",
                                        inset: 0,
                                        width: "100%",
                                        height: "100%",
                                        objectFit: "cover",
                                        display: "block",
                                        userSelect: "none",
                                    }}
                                />
                            ) : null}

                            {showTitle && (
                                <>
                                    {/* Gradient for legibility (matches corner) */}
                                    <div
                                        style={{
                                            position: "absolute",
                                            inset: 0,
                                            background: isTop
                                                ? "linear-gradient(0deg, rgba(0,0,0,0) 35%, rgba(0,0,0,0.7) 100%)"
                                                : "linear-gradient(180deg, rgba(0,0,0,0) 35%, rgba(0,0,0,0.7) 100%)",
                                            pointerEvents: "none",
                                        }}
                                    />

                                    {/* Title at chosen corner */}
                                    <div
                                        style={{
                                            position: "absolute",
                                            left: padLeft,
                                            right: padRight,
                                            [isTop ? "top" : "bottom"]: isTop
                                                ? padTop
                                                : padBottom,
                                            textAlign: isRight
                                                ? "right"
                                                : "left",
                                            pointerEvents: "none",
                                        }}
                                    >
                                        <span
                                            style={{
                                                color: titleColor,
                                                fontSize: 28,
                                                fontWeight: 700,
                                                lineHeight: "1.1em",
                                                letterSpacing: "-0.02em",
                                                whiteSpace: "pre-line",
                                                textShadow:
                                                    "0 2px 10px rgba(0,0,0,0.4)",
                                                ...(titleFont || {}),
                                            }}
                                        >
                                            {slide.title}
                                        </span>
                                    </div>
                                </>
                            )}

                            {/* Dim overlay (darkens inactive cards entirely) */}
                            <div
                                style={{
                                    position: "absolute",
                                    inset: 0,
                                    background: "#000000",
                                    opacity: isFront ? 0 : dim,
                                    transition: `opacity ${maskDuration}s ease-in-out`,
                                    pointerEvents: "none",
                                }}
                            />
                        </div>
                    )
                })}
            </div>
        </div>
    )
}

const COMPONENT_DEFAULTS = {
    slides: [
        {
            image: {
                src: "https://imagedelivery.net/IEUjvl3YUlxY-MrTpOAWDQ/7d4d2641-d6a8-4fef-e85c-b12ed100d500/w=800",
            },
            title: "James Walker",
        },
        {
            image: {
                src: "https://imagedelivery.net/IEUjvl3YUlxY-MrTpOAWDQ/933a7615-f4b6-4eae-8ed1-705fa0e24400/w=800",
            },
            title: "Olivia Carter",
        },
        {
            image: {
                src: "https://imagedelivery.net/IEUjvl3YUlxY-MrTpOAWDQ/859c75ea-953e-489e-be61-91a03a35d700/w=800",
            },
            title: "Amelia Foster",
        },
        {
            image: {
                src: "https://imagedelivery.net/IEUjvl3YUlxY-MrTpOAWDQ/31afae9c-5ba3-4ec3-2534-ed8198ed1100/w=800",
            },
            title: "Benjamin Harris",
        },
        {
            image: {
                src: "https://imagedelivery.net/IEUjvl3YUlxY-MrTpOAWDQ/ed7b1c40-3332-43d8-a9eb-4615ef341b00/w=800",
            },
            title: "Lucas Martin",
        },
    ],
    cardWidth: 400,
    cardHeight: 400,
    radius: 3,
    tilt: 12,
    sideTilt: 8,
    gap: 8,
    opacity: 60,
    inactiveCardsPerSide: 2,
    autoplay: false,
    autoplayDirection: "rightToLeft",
    transition: {
        type: "tween",
        duration: 0.6,
        delay: 2.5,
        ease: [0.22, 1, 0.36, 1],
    },
    showTitle: true,
    titleFont: {
        fontFamily: "Inter",
        variant: "Bold",
        fontSize: "28px",
        letterSpacing: "-0.02em",
        lineHeight: "1.1em",
    } as any,
    titleColor: "#ffffff",
    titlePosition: {
        position: "bottomLeft",
        paddingLeft: 22,
        paddingRight: 22,
        paddingTop: 24,
        paddingBottom: 24,
    },
}
